/**
 * Centralized WebSocket message handler for the Viking Ears game
 * This eliminates duplicated message handling logic across components
 */

export enum MessageType {
  PLAYER_ID = 'player_id',
  QUEUE_STATUS = 'queue_status',
  MATCH_FOUND = 'match_found',
  GAME_STATE_UPDATE = 'game_state_update',
  COMBAT_RESOLVED = 'combatResolved',
  ERROR = 'error',
  JOIN_QUEUE = 'join_queue',
  LEAVE_QUEUE = 'leave_queue',
  PLAY_CARD = 'play_card',
  GET_GAME_STATE = 'getGameState',
  START_COMBAT = 'startCombat',
  REVEAL_TILE = 'revealTile',
  SELECT_HERO = 'select_hero',
  TOGGLE_READY = 'toggle_ready',
  PLAYER_LIST = 'player_list',
  // Server message types
  CONNECTION_ESTABLISHED = 'connection_established',
  ECHO = 'echo'
}

export interface WebSocketMessage {
  type: MessageType;
  [key: string]: any;
}

export interface PlayerIdMessage extends WebSocketMessage {
  type: MessageType.PLAYER_ID;
  playerId: string;
}

export interface QueueStatusMessage extends WebSocketMessage {
  type: MessageType.QUEUE_STATUS;
  status: 'in_queue' | 'not_in_queue';
}

export interface MatchFoundMessage extends WebSocketMessage {
  type: MessageType.MATCH_FOUND;
  gameId: string;
  players: any[];
  gameState: any;
}

export interface GameStateUpdateMessage extends WebSocketMessage {
  type: MessageType.GAME_STATE_UPDATE;
  gameState: any;
}

export interface CombatResolvedMessage extends WebSocketMessage {
  type: MessageType.COMBAT_RESOLVED;
  payload: any;
}

export interface ErrorMessage extends WebSocketMessage {
  type: MessageType.ERROR;
  message: string;
}

export interface ConnectionEstablishedMessage extends WebSocketMessage {
  type: MessageType.CONNECTION_ESTABLISHED;
  message: string;
  token: string;
}

export interface EchoMessage extends WebSocketMessage {
  type: MessageType.ECHO;
  data: any;
  timestamp: number;
}

export class WebSocketMessageHandler {
  private static instance: WebSocketMessageHandler;
  private messageHandlers: Map<MessageType, ((message: WebSocketMessage) => void)[]> = new Map();

  private constructor() {}

  public static getInstance(): WebSocketMessageHandler {
    if (!WebSocketMessageHandler.instance) {
      WebSocketMessageHandler.instance = new WebSocketMessageHandler();
    }
    return WebSocketMessageHandler.instance;
  }

  /**
   * Add a message handler for a specific message type
   */
  public addMessageHandler(type: MessageType, handler: (message: WebSocketMessage) => void): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, []);
    }
    this.messageHandlers.get(type)!.push(handler);
  }

  /**
   * Remove a message handler for a specific message type
   */
  public removeMessageHandler(type: MessageType, handler: (message: WebSocketMessage) => void): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Process an incoming message and dispatch to appropriate handlers
   */
  public handleMessage(message: WebSocketMessage): void {
    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error(`Error in message handler for type ${message.type}:`, error);
        }
      });
    }
  }

  /**
   * Create a message object with proper typing
   */
  public createMessage(type: MessageType, payload: any = {}): WebSocketMessage {
    return {
      type,
      ...payload
    };
  }

  /**
   * Validate if a message has the correct structure
   */
  public isValidMessage(message: any): message is WebSocketMessage {
    return message && typeof message.type === 'string' && Object.values(MessageType).includes(message.type);
  }

  /**
   * Get all registered message types
   */
  public getRegisteredMessageTypes(): MessageType[] {
    return Array.from(this.messageHandlers.keys());
  }

  /**
   * Clear all message handlers
   */
  public clearAllHandlers(): void {
    this.messageHandlers.clear();
  }
}

export default WebSocketMessageHandler.getInstance();